// admin-ui/src/features/c/components/PhotosAddMore.tsx
import React, { useRef, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';

type Props = {
  locationId: number
  mode?: 'direct' | 'proposal'
  userId?: string | null
}

function slugify(name: string) {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._-]/g, '')
    .replace(/-+/g, '-')
    .slice(0, 120);
}

export default function PhotosAddMore({
  locationId,
  mode = 'direct',
  userId = null,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  async function handlePick() {
    inputRef.current?.click();
  }

  async function onFilesPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (mode === 'proposal' && !userId) {
      alert('Bitte zuerst einloggen, um Fotos vorzuschlagen.');
      return;
    }

    setBusy(true);

    try {
      for (const file of files) {
        const ext = file.name.split('.').pop() || 'jpg';
        const base = slugify(file.name.replace(/\.[^.]+$/, '') || 'upload');
        const ts = Date.now();

        if (mode === 'proposal') {
          const path = `locations/${locationId}/${userId}/${ts}-${base}.${ext}`;

          const { error: upErr } = await supabase.storage
            .from('photos-pending')
            .upload(path, file, {
              cacheControl: '3600',
              upsert: false,
              contentType: file.type || 'image/jpeg',
            });

          if (upErr) throw new Error('Upload fehlgeschlagen: ' + upErr.message);

          const { error: dbErr } = await supabase
            .from('photo_proposals')
            .insert({
              location_id: locationId,
              uploaded_by: userId,
              storage_path: path,
              public_url: null,
              caption: null,
              credit: null,
              license_confirmed: false,
              status: 'submitted',
            });

          if (dbErr) throw new Error('Foto-Vorschlag konnte nicht gespeichert werden: ' + dbErr.message);
        } else {
          const path = `locations/${locationId}/${ts}-${base}.${ext}`;

          const { error: upErr } = await supabase.storage
            .from('photos')
            .upload(path, file, {
              cacheControl: '3600',
              upsert: false,
              contentType: file.type || 'image/jpeg',
            });

          if (upErr) throw new Error('Upload fehlgeschlagen: ' + upErr.message);

          const { data: pub } = supabase.storage.from('photos').getPublicUrl(path);
          const public_url = pub?.publicUrl || null;

          const { width, height } = await readImageSize(file).catch(() => ({
            width: null,
            height: null,
          }));

          const payload = {
            location_id: locationId,
            public_url,
            storage_path: path,
            caption: null as string | null,
            author: null as string | null,
            preferred_width: null as number | null,
            width,
            height,
          };

          const res = await fetch('/api/user-photos', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          const j = await res.json().catch(() => ({}));
          if (!res.ok || !j?.ok) {
            throw new Error('DB-Insert fehlgeschlagen: ' + (j?.error || `HTTP ${res.status}`));
          }
        }
      }

      alert(mode === 'proposal' ? 'Foto-Vorschlag eingereicht.' : 'Gespeichert');

      if (mode === 'direct') {
        window.location.reload();
      }
    } catch (err: any) {
      console.error('[PhotosAddMore] Fehler:', err);
      alert(String(err?.message || err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFilesPicked}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        disabled={busy}
        onClick={handlePick}
        style={{
          padding: '8px 12px',
          border: '1px solid #cbd5e1',
          borderRadius: 10,
          background: '#111827',
          color: '#fff',
          fontWeight: 600,
        }}
      >
        {busy ? 'Lädt…' : mode === 'proposal' ? '+ Foto vorschlagen' : '+ Bilder auswählen'}
      </button>
    </div>
  );
}

/** Bildbreite/-höhe lokal aus der Datei lesen */
function readImageSize(file: File): Promise<{ width: number | null; height: number | null }> {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || null, height: img.naturalHeight || null });
      img.onerror = () => resolve({ width: null, height: null });
      const fr = new FileReader();
      fr.onload = () => {
        img.src = String(fr.result);
      };
      fr.onerror = reject;
      fr.readAsDataURL(file);
    } catch (e) {
      resolve({ width: null, height: null });
    }
  });
}
